import React, { useEffect, useState } from "react"
import { Alert, Badge, Button, Card, Container, Form, InputGroup, Spinner, Table } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { useHistory } from "react-router-dom"
import moment from "moment"
import API from "@/common/API"
import Settings from "@/common/Settings"
import { parsedUser } from "@/common/GetCurrentUser"
import { Routes } from "@/routes"
import VisitDetails from "@/features/visits/VisitDetails/VisitDetails"

interface PendingRevisitPlace {
  id: string
  name: string
  address?: string
  sector?: string
  city?: string
  image?: string
  image2?: string
  revisitAssignedTo?: string
  revisitAssignmentDate?: string | Date
  assignedToObj?: {
    firstName: string
    lastName: string
    email?: string
  }
  stage?: { name: string }
  placeStatus?: { name: string }
}

const PendingRevisitsPage: React.FC = () => {
  const { t } = useTranslation("global")
  const history = useHistory()
  const user = parsedUser()

  const [places, setPlaces] = useState<PendingRevisitPlace[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedPlace, setSelectedPlace] = useState<any | null>(null)

  // Notification State
  const [actionSuccess, setActionSuccess] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [sendingReminderId, setSendingReminderId] = useState<string | null>(null)
  const [isBatchSending, setIsBatchSending] = useState(false)

  const loadPendingRevisits = async () => {
    if (!user?.OrganizationId) return
    try {
      setIsLoading(true)
      const query = `OrganizationId=${user.OrganizationId}&revisitPending=true&includeVisitStats=false`
      const res = await API.getAction("place/list", query)
      if (res.data?.status === "ok") {
        setPlaces(res.data.response || [])
      }
    } catch (ex: any) {
      console.error("Error loading pending revisits:", ex)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadPendingRevisits()
  }, [user?.OrganizationId])

  const getImageUrl = (img?: string, img2?: string) => {
    const raw = img || img2
    if (!raw) return null
    return `${Settings.BasePathForAPI}file/getThumbnail?filename=${encodeURIComponent(raw)}`
  }

  const handleSendReminder = async (place: PendingRevisitPlace) => {
    const targetEmail = String(place.assignedToObj?.email || user?.Email || "")
    try {
      setSendingReminderId(place.id)
      setActionSuccess(null)
      setActionError(null)

      const query = `overrideEmail=${encodeURIComponent(targetEmail)}`
      const res = await API.getAction("Organization/SendRevisitRemindersNow", query)
      if (res.data?.status === "ok") {
        setActionSuccess(`Recordatorio de re-visita enviado con archivo de calendario .ics a ${targetEmail || "el agente"}.`)
      } else {
        setActionError(res.data?.message || "Error al enviar recordatorio.")
      }
    } catch (ex: any) {
      setActionError(ex.message || "Error de conexión al enviar recordatorio.")
    } finally {
      setSendingReminderId(null)
    }
  }

  const handleBatchSend = async () => {
    try {
      setIsBatchSending(true)
      setActionSuccess(null)
      setActionError(null)

      const res = await API.getAction("Organization/SendRevisitRemindersNow", "")
      if (res.data?.status === "ok") {
        setActionSuccess(`Boletines semanales de re-visita enviados a todos los agentes asignados (${res.data.response?.sentCount ?? 0} envíos).`)
      } else {
        setActionError(res.data?.message || "Error en envío masivo de recordatorios.")
      }
    } catch (ex: any) {
      setActionError(ex.message || "Error de conexión en envío masivo.")
    } finally {
      setIsBatchSending(false)
    }
  }

  const filteredPlaces = places.filter(p => {
    if (!searchTerm.trim()) return true
    const term = searchTerm.toLowerCase()
    const agentName = p.assignedToObj ? `${p.assignedToObj.firstName} ${p.assignedToObj.lastName}`.toLowerCase() : ""
    return (
      p.name.toLowerCase().includes(term) ||
      (p.address && p.address.toLowerCase().includes(term)) ||
      (p.sector && p.sector.toLowerCase().includes(term)) ||
      (p.city && p.city.toLowerCase().includes(term)) ||
      agentName.includes(term)
    )
  })

  return (
    <Container fluid className="px-4 py-3">
      {/* Header Banner */}
      <Card className="border-0 shadow-sm rounded-3 mb-4 bg-white">
        <Card.Body className="p-4 d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div className="d-flex align-items-center gap-3">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center bg-warning bg-opacity-10 text-warning"
              style={{ width: 56, height: 56, fontSize: 24 }}
            >
              <i className="fas fa-rotate" />
            </div>
            <div>
              <h4 className="fw-bold text-dark mb-1">
                Re-visitas Pendientes ({places.length})
              </h4>
              <p className="text-muted small mb-0">
                Listado consolidado de lugares con compromiso de re-visita asignado en la organización.
              </p>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <InputGroup size="sm" style={{ width: 280 }}>
              <InputGroup.Text className="bg-white border-end-0 text-muted">
                <i className="fas fa-search" />
              </InputGroup.Text>
              <Form.Control
                className="border-start-0 ps-0 shadow-none"
                placeholder="Buscar lugar, dirección o agente..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </InputGroup>

            <Button
              variant="warning"
              size="sm"
              className="fw-semibold text-dark text-nowrap rounded-3"
              disabled={isBatchSending || places.length === 0}
              onClick={handleBatchSend}
            >
              {isBatchSending ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Enviando...
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane me-1" />
                  Envío Masivo de Recordatorios
                </>
              )}
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Notifications */}
      {actionSuccess && <Alert variant="success" dismissible onClose={() => setActionSuccess(null)}>{actionSuccess}</Alert>}
      {actionError && <Alert variant="danger" dismissible onClose={() => setActionError(null)}>{actionError}</Alert>}

      {/* Places Table */}
      <Card className="border-0 shadow-sm rounded-3">
        <Card.Body className="p-0">
          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="warning" className="me-2" />
              <span className="small text-muted">Cargando re-visitas pendientes...</span>
            </div>
          ) : filteredPlaces.length === 0 ? (
            <Alert variant="light" className="text-center py-5 border-0 m-0 text-muted">
              <i className="fas fa-calendar-check fa-3x mb-3 text-warning opacity-50" />
              <h5>No hay re-visitas pendientes</h5>
              <p className="small mb-0">No se encontraron lugares con re-visita asignada bajo los criterios seleccionados.</p>
            </Alert>
          ) : (
            <Table hover responsive className="align-middle small mb-0">
              <thead className="table-light">
                <tr>
                  <th className="ps-4">FOTO</th>
                  <th>LUGAR / UBICACIÓN</th>
                  <th>AGENTE ASIGNADO</th>
                  <th>FECHA ASIGNACIÓN</th>
                  <th>ESTADO</th>
                  <th className="text-end pe-4">ACCIONES</th>
                </tr>
              </thead>
              <tbody>
                {filteredPlaces.map(p => {
                  const imgUrl = getImageUrl(p.image, p.image2)
                  const addressText = [p.address, p.sector, p.city].filter(Boolean).join(", ")
                  const agentName = p.assignedToObj ? `${p.assignedToObj.firstName} ${p.assignedToObj.lastName}` : "Sin agente"

                  return (
                    <tr key={p.id}>
                      <td className="ps-4" style={{ width: 64 }}>
                        {imgUrl ? (
                          <img loading="lazy"
                            src={imgUrl}
                            alt={p.name}
                            className="rounded-3 border object-fit-cover"
                            style={{ width: 44, height: 44 }}
                          />
                        ) : (
                          <div
                            className="rounded-3 bg-secondary bg-opacity-10 d-flex align-items-center justify-content-center fw-bold text-secondary border"
                            style={{ width: 44, height: 44 }}
                          >
                            {p.name.substring(0, 2).toUpperCase()}
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="fw-bold text-dark fs-6 mb-0">{p.name}</div>
                        <div className="text-muted small">
                          <i className="fas fa-location-dot me-1 text-danger" />
                          {addressText || "Sin dirección registrada"}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div
                            className="rounded-circle bg-primary bg-opacity-10 text-primary d-flex align-items-center justify-content-center fw-semibold"
                            style={{ width: 32, height: 32, fontSize: 12 }}
                          >
                            {agentName.substring(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <div className="fw-semibold text-dark mb-0">{agentName}</div>
                            {p.assignedToObj?.email && (
                              <small className="text-muted">{p.assignedToObj.email}</small>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="fw-semibold text-dark">
                          {p.revisitAssignmentDate ? moment(p.revisitAssignmentDate).format("DD/MM/YYYY") : "Reciente"}
                        </div>
                        <small className="text-muted">
                          {p.revisitAssignmentDate ? moment(p.revisitAssignmentDate).fromNow() : "—"}
                        </small>
                      </td>
                      <td>
                        <Badge bg="warning" text="dark" className="px-2 py-1 rounded-2">
                          <i className="fas fa-rotate me-1" />
                          Re-visita Pendiente
                        </Badge>
                      </td>
                      <td className="text-end pe-4">
                        <div className="d-flex align-items-center justify-content-end gap-2">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="fw-semibold rounded-2 px-2 py-1"
                            onClick={() => setSelectedPlace(p)}
                          >
                            <i className="fas fa-eye me-1" />
                            Ver Detalle
                          </Button>

                          <Button
                            variant="outline-warning"
                            size="sm"
                            className="fw-semibold text-dark rounded-2 px-2 py-1"
                            disabled={sendingReminderId === p.id}
                            onClick={() => handleSendReminder(p)}
                          >
                            {sendingReminderId === p.id ? (
                              <Spinner animation="border" size="sm" />
                            ) : (
                              <>
                                <i className="fas fa-envelope-open-text me-1" />
                                Recordatorio
                              </>
                            )}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Place Details Modal Drawer */}
      {selectedPlace && (
        <VisitDetails
          show={!!selectedPlace}
          placeId={selectedPlace.id}
          toggle={() => setSelectedPlace(null)}
        />
      )}
    </Container>
  )
}

export default PendingRevisitsPage
